import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 * @property {HTMLScriptElement} [allVariantsJson] - The embedded full variants JSON script.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#rebuildRadioCache();
    this.#syncOptionsVisibilityAndAvailability(this.#getAllVariants());

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Rebuilds internal radio and checked index caches from the current DOM.
   */
  #rebuildRadioCache() {
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.checked || radio.dataset.currentChecked === 'true'
      );
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });
  }

  /**
   * Parses the embedded full variants list JSON inside the variant picker.
   * @returns {Array<{id: number, title: string, option1: string | null, option2: string | null, option3: string | null, options: string[], available: boolean, featured_media?: any}>}
   */
  #getAllVariants() {
    const script =
      /** @type {HTMLScriptElement | null} */ (this.refs.allVariantsJson) ||
      this.querySelector('script[ref="allVariantsJson"]');
    if (!script?.textContent) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Synchronizes visibility and availability of Option 1 (Style), Option 2 (Color), and Option 3 (Size)
   * based on the currently selected Option 1 value.
   * @param {Array<{id: number, option1: string | null, option2: string | null, option3: string | null, options: string[], available: boolean}>} allVariants
   */
  #syncOptionsVisibilityAndAvailability(allVariants) {
    if (!allVariants || allVariants.length === 0) return;

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets?.length ? this.refs.fieldsets : Array.from(this.querySelectorAll('fieldset.variant-option'))
    );

    if (fieldsets.length > 1) {
      const opt1Input = /** @type {HTMLInputElement | null} */ (fieldsets[0]?.querySelector('input:checked'));
      const selectedStyle = opt1Input?.value;
      if (!selectedStyle) return;

      const styleVariants = allVariants.filter((v) => v.option1 === selectedStyle);
      if (styleVariants.length === 0) return;

      // Option 1 (Style): never mark disabled if at least 1 variant with that Style is available
      for (const input of fieldsets[0]?.querySelectorAll('input') ?? []) {
        const hasAvailable = allVariants.some((v) => v.option1 === input.value && v.available);
        if (hasAvailable) {
          input.removeAttribute('aria-disabled');
          input.dataset.optionAvailable = 'true';
          input.closest('label')?.querySelector('.variant-option__strikethrough')?.remove();
        }
      }

      // Option 2 (Color): show only colors that exist in the selected Style
      if (fieldsets[1]) {
        const validOpt2 = new Set(styleVariants.map((v) => v.option2));
        for (const input of fieldsets[1].querySelectorAll('input')) {
          const exists = validOpt2.has(input.value);
          const label = input.closest('label');
          if (label) {
            label.hidden = !exists;
            label.style.display = exists ? '' : 'none';
          }
          if (exists) {
            const hasAvailable = styleVariants.some((v) => v.option2 === input.value && v.available);
            if (hasAvailable) {
              input.removeAttribute('aria-disabled');
              input.dataset.optionAvailable = 'true';
              label?.querySelector('.variant-option__strikethrough')?.remove();
            }
          }
        }
        // Update swatch label text if present
        const checkedOpt2 = /** @type {HTMLInputElement | null} */ (fieldsets[1].querySelector('input:checked'));
        const swatchValueEl = fieldsets[1].querySelector('[data-selected-color-label], .variant-option__swatch-value');
        if (swatchValueEl && checkedOpt2) {
          swatchValueEl.textContent = checkedOpt2.value;
        }
      }

      // Sync any swatch value label across all fieldsets (e.g., if Color is Option 1)
      for (const fs of fieldsets) {
        const checkedInput = /** @type {HTMLInputElement | null} */ (fs.querySelector('input:checked'));
        const labelValEl = fs.querySelector('[data-selected-color-label], .variant-option__swatch-value');
        if (labelValEl && checkedInput) {
          labelValEl.textContent = checkedInput.value;
        }
      }

      // Option 3 (Size): show only sizes that exist in the selected Style
      if (fieldsets[2]) {
        const validOpt3 = new Set(styleVariants.map((v) => v.option3));
        for (const input of fieldsets[2].querySelectorAll('input')) {
          const exists = validOpt3.has(input.value);
          const label = input.closest('label');
          if (label) {
            label.hidden = !exists;
            label.style.display = exists ? '' : 'none';
          }
          if (exists) {
            const hasAvailable = styleVariants.some((v) => v.option3 === input.value && v.available);
            if (hasAvailable) {
              input.removeAttribute('aria-disabled');
              input.dataset.optionAvailable = 'true';
              label?.querySelector('.variant-option__strikethrough')?.remove();
            }
          }
        }
      }
      return;
    }

    // Fallback for <select> dropdowns if used
    const selects = Array.from(this.querySelectorAll('select.variant-option__select'));
    if (selects.length > 1) {
      const selectedStyle = selects[0]?.value;
      if (!selectedStyle) return;
      const styleVariants = allVariants.filter((v) => v.option1 === selectedStyle);
      if (styleVariants.length === 0) return;

      if (selects[1]) {
        const validOpt2 = new Set(styleVariants.map((v) => v.option2));
        for (const opt of selects[1].options) {
          const exists = validOpt2.has(opt.value);
          opt.hidden = !exists;
          opt.disabled = !exists;
        }
      }
      if (selects[2]) {
        const validOpt3 = new Set(styleVariants.map((v) => v.option3));
        for (const opt of selects[2].options) {
          const exists = validOpt3.has(opt.value);
          opt.hidden = !exists;
          opt.disabled = !exists;
        }
      }
    }
  }

  /**
   * Resolves a valid variant when the user changes an option, automatically selecting the first
   * valid Color/Size if the current combination does not exist in the newly selected Style.
   * @param {HTMLElement} changedTarget
   * @returns {{id: number, options: string[], available: boolean} | null}
   */
  #resolveVariantSelection(changedTarget) {
    const allVariants = this.#getAllVariants();
    if (allVariants.length === 0) return null;

    this.#syncOptionsVisibilityAndAvailability(allVariants);

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets?.length ? this.refs.fieldsets : Array.from(this.querySelectorAll('fieldset.variant-option'))
    );

    if (fieldsets.length > 0) {
      const currentValues = fieldsets.map((fs) => {
        const checked = /** @type {HTMLInputElement | null} */ (fs.querySelector('input:checked'));
        return checked ? checked.value : '';
      });

      let matchedVariant = allVariants.find((v) =>
        currentValues.every((val, idx) => v.options[idx] === val)
      );

      if (!matchedVariant) {
        const changedFieldsetIndex =
          changedTarget.dataset?.fieldsetIndex !== undefined
            ? Number.parseInt(changedTarget.dataset.fieldsetIndex, 10)
            : 0;
        const clickedValue = currentValues[changedFieldsetIndex];

        const candidatesForClicked = allVariants.filter((v) => v.options[changedFieldsetIndex] === clickedValue);
        const availableCandidates = candidatesForClicked.filter((v) => v.available);
        const pool = availableCandidates.length > 0 ? availableCandidates : candidatesForClicked;

        if (changedFieldsetIndex === 0) {
          const curColor = currentValues[1];
          const curSize = currentValues[2];
          matchedVariant =
            (curColor && curSize && pool.find((v) => v.option2 === curColor && v.option3 === curSize)) ||
            (curColor && pool.find((v) => v.option2 === curColor)) ||
            (curSize && pool.find((v) => v.option3 === curSize)) ||
            pool[0] ||
            allVariants[0];
        } else if (changedFieldsetIndex === 1) {
          const curStyle = currentValues[0];
          const curSize = currentValues[2];
          matchedVariant =
            (curStyle && curSize && pool.find((v) => v.option1 === curStyle && v.option3 === curSize)) ||
            (curStyle && pool.find((v) => v.option1 === curStyle)) ||
            (curSize && pool.find((v) => v.option3 === curSize)) ||
            pool[0] ||
            allVariants[0];
        } else {
          const curStyle = currentValues[0];
          const curColor = currentValues[1];
          matchedVariant =
            (curStyle && curColor && pool.find((v) => v.option1 === curStyle && v.option2 === curColor)) ||
            (curStyle && pool.find((v) => v.option1 === curStyle)) ||
            (curColor && pool.find((v) => v.option2 === curColor)) ||
            pool[0] ||
            allVariants[0];
        }

        if (matchedVariant) {
          fieldsets.forEach((fs, idx) => {
            const desiredValue = matchedVariant.options[idx];
            if (!desiredValue) return;
            const currentChecked = /** @type {HTMLInputElement | null} */ (fs.querySelector('input:checked'));
            if (!currentChecked || currentChecked.value !== desiredValue) {
              const targetRadio = Array.from(fs.querySelectorAll('input')).find((r) => r.value === desiredValue);
              if (targetRadio) {
                this.updateSelectedOption(targetRadio);
              }
            }
          });
          this.#syncOptionsVisibilityAndAvailability(allVariants);
        }
      }

      return matchedVariant || null;
    }

    // Fallback for <select> dropdowns
    const selects = Array.from(this.querySelectorAll('select.variant-option__select'));
    if (selects.length > 0) {
      const currentValues = selects.map((s) => s.value);
      let matchedVariant = allVariants.find((v) =>
        currentValues.every((val, idx) => v.options[idx] === val)
      );

      if (!matchedVariant) {
        const changedSelect = changedTarget.closest('select');
        const changedIdx = changedSelect ? Math.max(0, selects.indexOf(changedSelect)) : 0;
        const clickedValue = currentValues[changedIdx];
        const candidates = allVariants.filter((v) => v.options[changedIdx] === clickedValue);
        const avail = candidates.filter((v) => v.available);
        const pool = avail.length > 0 ? avail : candidates;
        const curOpt1 = currentValues[0];
        const curOpt2 = currentValues[1];
        const curOpt3 = currentValues[2];

        matchedVariant =
          (curOpt2 && pool.find((v) => v.option2 === curOpt2)) ||
          (curOpt3 && pool.find((v) => v.option3 === curOpt3)) ||
          (curOpt1 && pool.find((v) => v.option1 === curOpt1)) ||
          pool[0] ||
          allVariants[0];

        if (matchedVariant) {
          selects.forEach((s, idx) => {
            const desiredValue = matchedVariant.options[idx];
            if (desiredValue && s.value !== desiredValue) {
              s.value = desiredValue;
              this.updateSelectedOption(s);
            }
          });
          this.#syncOptionsVisibilityAndAvailability(allVariants);
        }
      }

      return matchedVariant || null;
    }

    return null;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const matchedVariant = this.#resolveVariantSelection(event.target);
    if (matchedVariant?.id) {
      selectedOption.dataset.variantId = String(matchedVariant.id);
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          for (let i = 0; i < radios.length; i++) {
            const r = radios[i];
            if (!r) continue;
            if (i === newCurrentIndex) {
              r.dataset.currentChecked = 'true';
              r.checked = true;
            } else {
              r.dataset.currentChecked = 'false';
              r.checked = false;
            }
          }

          if (newPreviousIndex !== undefined && newPreviousIndex !== newCurrentIndex && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          const swatchValueEl = fieldset.querySelector('[data-selected-color-label], .variant-option__swatch-value');
          if (swatchValueEl) {
            swatchValueEl.textContent = target.value;
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      const parentFieldset = target.closest('fieldset');
      const fallbackSwatchVal = parentFieldset?.querySelector('[data-selected-color-label], .variant-option__swatch-value');
      if (fallbackSwatchVal) {
        fallbackSwatchVal.textContent = target.value;
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    } else if (selectedOption.dataset.variantId) {
      params.push(`variant=${selectedOption.dataset.variantId}`);
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#rebuildRadioCache();
    this.#syncOptionsVisibilityAndAvailability(this.#getAllVariants());
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
